import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { Box } from "@mui/system";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ChangeServiceReport, MachinePartGroup, ServiceReportFile, ServiceReportFileTypeCode } from "../../models/serviceReportsModels";
import { DateField, DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  PublicState,
  changeServiceReport,
  createServiceReport,
  getMachineParts,
  getMachine,
  getServiceReport,
  getServiceReports
} from "../../publicSlice";
import ReportFile from "../../../shared/ReportFile";
import GoBack from "../shared/GoBack";
import { MAX_FILE_SIZE_MB } from "../../../shared/models/models";
import Loader from "../../../shared/components/Loader";
import useTranslation from "../../../shared/hooks/useTranslation";
import KioskBackButton from "../kiosk/KioskBackButton";

// ── Hardcoded fallback groups (same as desktop CreateServiceReportPage) ──
const MACHINE_PART_GROUPS = [
  { groupKey: "spindleSystem", parts: [
    { key: "mainSpindle", en: "Main spindle" }, { key: "spindleMotor", en: "Spindle motor" },
    { key: "spindleBearings", en: "Spindle bearings" }, { key: "toolTaperDrawbar", en: "Tool taper / drawbar" },
  ] },
  { groupKey: "axisDrives", parts: [
    { key: "servoMotors", en: "Servo motors" }, { key: "ballScrews", en: "Ball screws" },
    { key: "linearGuidesRails", en: "Linear guides / rails" }, { key: "axisBearings", en: "Axis bearings" },
  ] },
  { groupKey: "cncControl", parts: [
    { key: "controllerHmi", en: "Controller / HMI" }, { key: "ioBoards", en: "I/O boards" },
    { key: "plcModule", en: "PLC module" }, { key: "softwareFirmware", en: "Software / firmware" },
  ] },
  { groupKey: "electrical", parts: [
    { key: "powerSupply", en: "Power supply" }, { key: "cablesWiring", en: "Cables / wiring" },
    { key: "sensorsSwitches", en: "Sensors / switches" }, { key: "contactorsRelays", en: "Contactors / relays" },
  ] },
  { groupKey: "hydraulicSystem", parts: [
    { key: "hydraulicPump", en: "Hydraulic pump" }, { key: "cylinders", en: "Cylinders" },
    { key: "valves", en: "Valves" }, { key: "hosesFittings", en: "Hoses / fittings" },
  ] },
  { groupKey: "pneumaticSystem", parts: [
    { key: "airSystem", en: "Air system" }, { key: "pneumaticValves", en: "Pneumatic valves" },
    { key: "pneumaticCylinders", en: "Pneumatic cylinders" },
  ] },
  { groupKey: "coolantSystem", parts: [
    { key: "coolantPump", en: "Coolant pump" }, { key: "nozzles", en: "Nozzles" },
    { key: "tankFiltration", en: "Tank / filtration" },
  ] },
  { groupKey: "lubrication", parts: [
    { key: "lubricationPump", en: "Lubrication pump" }, { key: "distributionLines", en: "Distribution lines" },
  ] },
  { groupKey: "toolChanger", parts: [
    { key: "atcMechanism", en: "ATC mechanism" }, { key: "toolMagazine", en: "Tool magazine" },
    { key: "gripperArm", en: "Gripper arm" },
  ] },
  { groupKey: "turretLathes", parts: [
    { key: "turretMechanism", en: "Turret mechanism" }, { key: "toolHolders", en: "Tool holders" },
    { key: "indexingMotor", en: "Indexing motor" },
  ] },
  { groupKey: "workholding", parts: [
    { key: "chuckCollet", en: "Chuck / collet" }, { key: "jaws", en: "Jaws" },
    { key: "tailstock", en: "Tailstock" }, { key: "steadyRest", en: "Steady rest" },
  ] },
  { groupKey: "chipManagement", parts: [
    { key: "chipConveyor", en: "Chip conveyor" }, { key: "chipAuger", en: "Chip auger" },
  ] },
  { groupKey: "enclosureSafety", parts: [
    { key: "doorsGuards", en: "Doors / guards" }, { key: "interlocks", en: "Interlocks" },
    { key: "wayCovers", en: "Way covers" },
  ] },
  { groupKey: "consumables", parts: [
    { key: "filters", en: "Filters" }, { key: "belts", en: "Belts" },
    { key: "sealsORings", en: "Seals / O-rings" }, { key: "wipers", en: "Wipers" },
  ] },
  { groupKey: "other", parts: [
    { key: "other", en: "Other" },
  ] },
];

interface PartOption {
  label: string;
  group: string;
  englishName: string;
  categoryId?: string;
}

export const CreateServiceReport = () => {
  const { machine, machineParts, serviceReport } = useAppSelector<PublicState>((state: any) => state.public);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { translate } = useTranslation(["public", "servicing"]);
  const { translate: tCommon } = useTranslation("common");
  const [searchParams] = useSearchParams();

  // Recover machine context from query param if Redux state was lost (e.g., page refresh)
  useEffect(() => {
    if (!machine) {
      const machineNumber = searchParams.get("machine");
      if (machineNumber) {
        dispatch(getMachine(machineNumber));
      }
    }
  }, [machine, searchParams, dispatch]);

  const goToMachine = () => {
    if (machine?.internalNumber) {
      navigate(`/public/machines/${machine.internalNumber}`);
    }
  };

  const {
    register,
    watch,
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors }
  } = useForm<ChangeServiceReport>({
    defaultValues: {
      title: "",
      description: "",
      notes: "",
      alarmCode: "",
      urgency: undefined,
      openDate: undefined,
      affectedPartCategoryId: "",
      files: null
    }
  });

  const [openDate] = watch(["openDate"]);

  const [newFiles, setFiles] = useState<ServiceReportFile[]>(serviceReport?.files || []);
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  const { id } = useParams();
  const isNew = Boolean(!id);

  // ── Build grouped part options from Machinery API ────────
  const partOptions: PartOption[] = useMemo(() => {
    const opts: PartOption[] = [];
    if (machineParts && machineParts.length > 0) {
      for (const group of machineParts) {
        const groupLabel = translate(`servicing:machineParts.groups.${group.groupKey}`);
        for (const part of group.parts) {
          const translationKey = part.code.replace(/_([a-z])/g, (_m: string, c: string) => c.toUpperCase());
          const partLabel = translate(`servicing:machineParts.parts.${translationKey}`);
          opts.push({ label: partLabel, group: groupLabel, englishName: part.name, categoryId: part.id });
        }
      }
    } else {
      // Fallback to hardcoded groups if API hasn't loaded yet
      for (const group of MACHINE_PART_GROUPS) {
        const groupLabel = translate(`servicing:machineParts.groups.${group.groupKey}`);
        for (const part of group.parts) {
          const partLabel = translate(`servicing:machineParts.parts.${part.key}`);
          opts.push({ label: partLabel, group: groupLabel, englishName: part.en });
        }
      }
    }
    return opts;
  }, [machineParts, translate]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files ? Array.from(event.target.files) : [];

    selectedFiles.forEach((file, i) => {
      processFile(file);
    });

    event.target.value = "";
  };

  const processFile = async (file: File) => {
    setError(undefined);

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError("File is too big");
      return;
    }

    const url = await detectUrl(file);

    if (url) {
      const newFile: ServiceReportFile = {
        name: file.name,
        content: url,
        typeCode: ServiceReportFileTypeCode.reportDocument,
        size: file.size
      };

      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles, newFile];
        return updatedFiles;
      });
    }
  };

  const detectUrl = (file: Blob | null) => {
    return new Promise<string | null>((resolve, reject) => {
      if (!file) {
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleDelete = (fileIndex: number) => {
    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter((_, i) => i !== fileIndex);
      return updatedFiles;
    });
  };

  const onSubmit: SubmitHandler<ChangeServiceReport> = async (data: ChangeServiceReport) => {
    await handleCreateServiceReport(data);
  };

  const handleCreateServiceReport = async (model: ChangeServiceReport) => {
    if (!machine?.internalNumber) {
      setSubmitError(translate("public:machineContextMissing"));
      return;
    }

    setSubmitting(true);
    setSubmitError(undefined);

    try {
      model.openDate = model.openDate || moment().toDate();
      model.files = newFiles;

      if (isNew) {
        const result = await dispatch(createServiceReport({ model, machineNumber: machine.internalNumber }));
        if (createServiceReport.rejected.match(result)) {
          setSubmitError(translate("public:submitFailed"));
          return;
        }
        await dispatch(getServiceReports({ machineNumber: machine.internalNumber, query: { statusCodes: ["InProgress", "Solved"] } }));
        navigate(`/public/machines/${machine.internalNumber}`);
      } else {
        const result = await dispatch(changeServiceReport({ model, serviceReportNumber: serviceReport?.number }));
        if (changeServiceReport.rejected.match(result)) {
          setSubmitError(translate("public:submitFailed"));
          return;
        }
        await dispatch(getServiceReports({ machineNumber: machine.internalNumber, query: { statusCodes: ["InProgress", "Solved"] } }));
        navigate(`/public/service-reports/${serviceReport?.number}`);
      }
    } catch {
      setSubmitError(translate("public:submitFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  // Load machine parts from Machinery API (replaces old getCategories)
  useEffect(() => {
    dispatch(getMachineParts());
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      dispatch(getServiceReport(id));
    }
  }, [id]);

  useEffect(() => {
    if (!isNew && serviceReport) {
      const changeModel: ChangeServiceReport = {
        title: serviceReport.title,
        description: serviceReport.description,
        notes: serviceReport.notes || "",
        alarmCode: serviceReport.alarmCode || "",
        urgency: serviceReport.urgency,
        openDate: serviceReport.openDate,
        affectedPartCategoryId: serviceReport.affectedPartCategoryId,
        files: serviceReport.files
      };
      reset(changeModel, { keepDefaultValues: true });
      setFiles(changeModel?.files || [])
    } else {
      reset();
      setFiles([]);
    }
  }, [serviceReport, isNew, reset]);

  if (!serviceReport && !isNew) {
    return <Loader />;
  }

  return (
    <Box className="h-full max-w-[1023px] mx-auto" sx={{ px: 2, pb: "80px" }}>
      <GoBack title={isNew ? translate("public:createServiceReport") : translate("public:editServiceReport")} />

      <Box className="mt-4" sx={{ height: "calc(100% - 100px)" }}>
        <form id="form" className="h-full" onSubmit={handleSubmit(onSubmit)}>
          <Box className="flex flex-col justify-between h-full">
            <Box className="flex flex-col gap-6">
              <Box>
                <InputLabel>{translate("public:reportTitle")}</InputLabel>
                <TextField
                  className="w-full"
                  {...register("title", {
                    required: tCommon("forms.required"),
                    maxLength: { value: 100, message: translate("public:titleTooLong") }
                  })}
                  inputProps={{ maxLength: 100 }}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              </Box>

              {/* ── ALARM CODE ── */}
              <Box>
                <InputLabel>{translate("public:alarmCode")}</InputLabel>
                <TextField
                  className="w-full"
                  placeholder={translate("public:alarmCodePlaceholder")}
                  {...register("alarmCode", {
                    maxLength: { value: 100, message: translate("public:alarmCodeTooLong") }
                  })}
                  inputProps={{ maxLength: 100 }}
                  error={!!errors.alarmCode}
                  helperText={errors.alarmCode?.message}
                />
              </Box>

              {/* ── URGENCY ── */}
              <Box>
                <InputLabel>{translate("public:urgency")}</InputLabel>
                <FormControl className="w-full">
                  <Controller
                    control={control}
                    name="urgency"
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                        displayEmpty
                      >
                        <MenuItem value="">
                          {translate("public:urgencyNone")}
                        </MenuItem>
                        <MenuItem value={1}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#C0392B" }} />
                            {translate("public:urgencyUrgent")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={2}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#E67E22" }} />
                            {translate("public:urgencyHigh")}
                          </Box>
                        </MenuItem>
                        <MenuItem value={4}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#27AE60" }} />
                            {translate("public:urgencyLow")}
                          </Box>
                        </MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Box>

              <Box>
                <InputLabel>{translate("public:openDate")}</InputLabel>

                <FormControl className="w-full">
                  <Controller
                    control={control}
                    name="openDate"
                    rules={{
                      validate: (value) => {
                        if (value) {
                          if (moment(value) > moment()) {
                            return translate("public:openDateFuture");
                          }
                        }
                      }
                    }}
                    render={({ field }) => (
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          {...field}
                          value={moment(field.value)}
                          onChange={(value) => field.onChange(value?.toDate())}
                          format="DD.MM.YYYY"
                          disableFuture
                        />
                      </LocalizationProvider>
                    )}
                  />
                </FormControl>
              </Box>

              <Box>
                <InputLabel>{translate("public:time")}</InputLabel>
                <FormControl className="w-full">
                  <Controller
                    control={control}
                    name="openDate"
                    render={({ field }) => (
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateField
                          {...field}
                          value={moment(openDate)}
                          onChange={(value) => field.onChange(value?.toDate())}
                          format="HH:mm"
                        />
                      </LocalizationProvider>
                    )}
                  />
                  <FormHelperText error>{errors.openDate?.message}</FormHelperText>
                </FormControl>
              </Box>

              {/* ── AFFECTED MACHINE PART (grouped autocomplete) ── */}
              <Box>
                <InputLabel>{translate("public:affectedMachinePart")}</InputLabel>
                <FormControl className="w-full">
                  <Controller
                    control={control}
                    name="affectedPartCategoryId"
                    rules={{ required: tCommon("forms.required") }}
                    render={({ field }) => {
                      const selectedOption = partOptions.find((o) => o.categoryId === field.value) || null;
                      return (
                        <Autocomplete
                          options={partOptions.filter((o) => !!o.categoryId)}
                          groupBy={(option) => option.group}
                          getOptionLabel={(option) => option.label}
                          value={selectedOption}
                          onChange={(_e, newValue) => {
                            field.onChange(newValue?.categoryId || "");
                            setTimeout(() => trigger("affectedPartCategoryId"));
                          }}
                          isOptionEqualToValue={(option, value) => option.categoryId === value.categoryId}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={!!errors.affectedPartCategoryId}
                              placeholder={translate("public:affectedMachinePart")}
                            />
                          )}
                          renderGroup={(params) => (
                            <Box key={params.key}>
                              <Box
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.8px",
                                  color: "#6B7280",
                                  px: 2,
                                  pt: 1.5,
                                  pb: 0.5,
                                  position: "sticky",
                                  top: 0,
                                  backgroundColor: "#fff",
                                  zIndex: 1,
                                }}
                              >
                                {params.group}
                              </Box>
                              {params.children}
                            </Box>
                          )}
                          renderOption={(props, option) => (
                            <Box
                              component="li"
                              {...props}
                              sx={{
                                fontSize: "14px !important",
                                pl: "32px !important",
                                py: "6px !important",
                              }}
                            >
                              {option.label}
                            </Box>
                          )}
                          slotProps={{
                            paper: {
                              sx: {
                                maxHeight: 350,
                              },
                            },
                          }}
                        />
                      );
                    }}
                  />
                  <FormHelperText error>{errors.affectedPartCategoryId?.message}</FormHelperText>
                </FormControl>
              </Box>

              <Box>
                <InputLabel>{tCommon("description")}</InputLabel>
                <TextField
                  className="w-full textarea"
                  multiline
                  rows={6}
                  {...register("description")}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  inputProps={{ maxLength: 2000 }}
                />
              </Box>

              {/* ── TECHNICAL NOTES ── */}
              <Box>
                <InputLabel>{translate("public:technicalNotes")}</InputLabel>
                <TextField
                  className="w-full textarea"
                  multiline
                  rows={4}
                  placeholder={translate("public:technicalNotesPlaceholder")}
                  {...register("notes")}
                  inputProps={{ maxLength: 4000 }}
                />
              </Box>

              <Box>
                <label htmlFor="file-input">
                  <Button component="span" variant="outlined" className="w-full">
                    {translate("public:uploadImage")}
                  </Button>
                  {!!error && (
                    <FormHelperText
                      error
                      sx={{
                        padding: "6px 8px",
                        margin: 0,
                        textAlign: "center",
                        minHeight: "40px",
                      }}
                    >
                      {error === "File is too big" ? translate("public:fileTooBig") : error}
                    </FormHelperText>
                  )}
                </label>

                <Box>
                  <TextField
                    type="file"
                    id="file-input"
                    sx={{ display: "none" }}
                    inputProps={{ multiple: true }}
                    onChange={handleFileChange}
                  />

                  {newFiles.length > 0 && (
                    <Box className="flex flex-col gap-2" sx={{ mt: 1 }}>
                      {newFiles.map((file, i) => (
                        <ReportFile
                          key={i}
                          file={file}
                          onDelete={() => handleDelete(i)}
                          canDownload={isNew ? false : true}
                          fileIndex={i}
                          isNew={isNew}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {submitError && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSubmitError(undefined)}>
                {submitError}
              </Alert>
            )}

            <Box className="flex gap-4 pt-4">
              <Button variant="outlined" className="w-full" onClick={goToMachine} disabled={submitting}>
                {tCommon("buttons.cancel")}
              </Button>

              <Button form="form" type="submit" className="w-full" variant="contained" disabled={submitting}>
                {submitting ? <CircularProgress size={24} color="inherit" /> : (isNew ? tCommon("buttons.add") : tCommon("buttons.save"))}
              </Button>
            </Box>
          </Box>
        </form>
      </Box>

      <KioskBackButton />
    </Box>
  );
};

export default CreateServiceReport;
